
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { UserX, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

interface DeleteUserDialogProps {
  userId: string;
}

interface DeleteUserResponse {
  success: boolean;
}

const DeleteUserDialog = ({ userId }: DeleteUserDialogProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleDeleteUser = async () => {
    try {
      // First wipe all user data
      const { data: wipeData, error: wipeError } = await supabase.functions.invoke<DeleteUserResponse>('wipe-user-data', {
        body: { userId },
      });

      if (wipeError) throw wipeError;

      // Delete user role
      const { error: roleError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId);

      if (roleError) throw roleError;

      // Delete the user through the Edge Function
      const { data, error } = await supabase.functions.invoke<DeleteUserResponse>('delete-user', {
        body: { userId },
      });

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['users'] });
      
      toast({
        title: "Success",
        description: "User has been completely deleted",
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: "Error",
        description: "Failed to delete user",
        variant: "destructive",
      });
    }
  };

  const handleLoginAsUser = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('admin-login-as-user', {
        body: { userId },
      });
      if (error) throw error;
      
      const newWindow = window.open(data.data.properties.action_link+"developer-dashboard", '_blank');
      
    } catch (error) {
      console.error('Error logging in as user:', error);
      toast({
        title: "Error",
        description: "Failed to login as user",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" onClick={handleLoginAsUser}>
        <ExternalLink className="h-4 w-4" />
      </Button>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="destructive" size="sm">
            <UserX className="h-4 w-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the user and all their associated data. The user will no longer be able to log in. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DeleteUserDialog;
